import { FC, useEffect, useState, useMemo } from "react";
import dayjs from "dayjs";
import cx from "classnames";
import Dialog, {
  DialogContent,
  DialogTitle,
  DialogActions,
} from "@/components/Dialog";
import {
  useDialog,
  useOpenExternalURL,
  useTrackers,
  useSnackbar,
  useResponsive,
  useLock,
  useFeatureFlags,
  useTripType,
} from "@/hooks";
import Button from "@/components/Button";
import {
  LocationIcon,
  CalenderCheckIcon,
  RedirectIcon,
  IndustryIcon,
  BuildingIcon,
  CheckedIcon,
  GlobalDollarIcon,
  PictureIcon,
} from "@/components/Icon";
import Checkbox from "@/components/Checkbox";
import Divider from "@/components/Divider";
import Typography from "@/components/Typography";
import Tag from "@/components/Tag";
import { useJobQuery } from "@/fetch/suppliers";
import {
  useCreateInterestedJobMutation,
  useCreateSavedJobMutation,
  useDeleteSavedJobMutation,
} from "@/fetch/trip";
import { Skeleton } from "@/components/Loader";
import { useSearchJobsQuery, JOB_STATUS } from "@/fetch/suppliers";
import {
  useUpdateInterestedJobsBulkMutation,
  useGeneralSettingsQuery,
} from "@/fetch/trip";
import Tooltip from "@/components/Tooltip";
import useCategory from "./useCategory";
import useStartDates from "./useStartDates";
import styles from "./JobDialog.module.scss";

const INTEREST_JOBS_LIMIT = 5;
const TRIP_SETTINGS_JOB_TAB_LOCKED_NAME = "lock_jobs"; // make it to read from one place

type Props = {};

const JobDialog: FC<Props> = ({}) => {
  const [showSuccess, setShowSuccess] = useState<boolean>(false);
  const [showFallbackImage, setShowFallbackImage] = useState<boolean>(false);
  const [isAgreedToTerms, setIsAgreedToTerms] = useState<boolean>(false);
  const { query, onClose } = useDialog("job");
  const { id } = query;
  const { track } = useTrackers();
  const { enqueueSnackbar } = useSnackbar();
  const { isMobile } = useResponsive();
  const {
    flags: { disableShowInterestButton },
  } = useFeatureFlags();
  const { isSuperliteTrip, isWHArrivalOnly } = useTripType();

  const { data: job, isLoading } = useJobQuery(id as string);

  const {
    data: interestedJobsData,
    isLoading: isLoadingInterestedJobs,
    refetch,
  } = useSearchJobsQuery(
    {
      page: 1,
      limit: 30,
      isInterested: true,
    },
    ""
  );

  const { mutateAsync: createInterest, isLoading: isCreatingInterest } =
    useCreateInterestedJobMutation();
  const { mutateAsync: createSave, isLoading: isCreatingSave } =
    useCreateSavedJobMutation();
  const { mutateAsync: removeSave, isLoading: isRemovingSave } =
    useDeleteSavedJobMutation();
  const { mutateAsync: updateJobs, isLoading: isUpdatingJobs } =
    useUpdateInterestedJobsBulkMutation();
  const { data: generalSettingsData, isLoading: isLoadingGeneralSettings } =
    useGeneralSettingsQuery();

  const openExternalURL = useOpenExternalURL();

  const interestedJobs = interestedJobsData?.data || [];
  const interestedJobsNumber = interestedJobs.filter(
    (eachJob) => eachJob.status === JOB_STATUS.Active
  ).length;

  const isJobTabLocked = useMemo(
    () =>
      ((generalSettingsData?.result || []).find(
        (each) => each.name === TRIP_SETTINGS_JOB_TAB_LOCKED_NAME
      )?.value as boolean) || false,
    [generalSettingsData]
  );

  const isExternal = job?._cb === 16;

  const canExpressInterestInJob =
    interestedJobsNumber < INTEREST_JOBS_LIMIT &&
    !isJobTabLocked &&
    !isExternal &&
    !isLoading;

  const isSaved = job?.is_saved;
  const isInterested = Boolean(job?.is_interested);
  const postedDate = isExternal
    ? Boolean(job?.post_date)
      ? `Job Posted on ${dayjs(Number(job?.post_date) * 1000).format("MMMM")}`
      : "N/A"
    : Boolean(job?.start_date)
    ? `Job Start Month ${dayjs(Number(job?.start_date) * 1000).format("MMMM")}`
    : "N/A";
  const getStartDateMonths = useStartDates();
  const startDates = getStartDateMonths(job?.dates || []);

  const jobStartDateLabel = isExternal
    ? postedDate
    : `Start month: ${startDates}`;

  const logoImageUrl = `https://gwatco-res.cloudinary.com/image/upload/v1684225405/${job?.s3_logo}`;

  useEffect(() => {
    track("Job Dialog Opened", {
      eventId: "job-dialog-opened",
      jobId: id,
    });
    refetch();
  }, []);

  const categoryObj = useCategory(job?.category_list);

  const handleSave = async () => {
    try {
      if (!job) return;
      if (isSaved) {
        await removeSave({ savedJobId: job.saved_id || 0 });
        enqueueSnackbar("Unsaved Successfully!", {
          variant: "success",
        });
        track("Job Unsaved", {
          eventId: "job-unsaved",
          jobId: job?.id,
        });
        onClose({ id });
      } else {
        await createSave({ job_id: job.id });
        enqueueSnackbar("Saved Successfully!", {
          variant: "success",
        });
        track("Job Saved", {
          eventId: "job-saved",
          jobId: job?.id,
        });
        onClose({ id });
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Sorry, something went wrong!", {
        variant: "error",
      });
    }
  };

  const handleInterest = async () => {
    try {
      if (!job) return;
      if (isLoadingInterestedJobs) return;
      if (!canExpressInterestInJob) return;
      if (interestedJobs.length) {
        await updateJobs(
          interestedJobs.map((each, idx) => ({
            id: each.interested_id || 0,
            job_id: each.id,
            order: idx + 2,
          }))
        );
      }

      await createInterest({
        job_id: job.id,
        order: 1,
      });

      track("Interested Job Added", {
        eventId: "interested-job-added",
        jobId: job?.id,
      });

      setShowSuccess(true);
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Sorry, something went wrong!", {
        variant: "error",
      });
    }
  };

  const goToJob = () => {
    openExternalURL(job?.source_url || "");
    track("Job Link Clicked", {
      eventId: "job-link-clicked",
      jobId: job?.id || "",
    });
  };

  const handleOpenWebsite = () => {
    if (!job || !job.website) return;
    openExternalURL(job.website);
  };

  const handleClose = () => {
    onClose({ id });
  };

  // const requirements = [
  //   `You’ll have an NVQ Level 3 or equivalent spa qualification, and ideally have previous experience in a similar role`,
  //   `You’ll be excellent at building a rapport with our guests and be passionate about making them feel relaxed, indulged and inspired`,
  //   `You’ll feel confident in acting as an expert and giving advice to guests on suitable products that will match their personal needs`,
  //   `You’ll have a streak of sales in you, looking for opportunities to recommend spa products and return treatments`,
  //   `Most of all, you’ll have a friendly, welcoming and genuine manner`,
  // ];

  const skills = Boolean(job?.skill_set) ? job?.skill_set.split(",") || [] : [];

  const isMutating =
    isCreatingSave || isRemovingSave || isCreatingInterest || isUpdatingJobs;

  const addFallbackImage = () => {
    setShowFallbackImage(true);
  };

  const getDomain = () => {
    if (!job?.source_url) return "";
    let currentUrl = job?.source_url || "";
    if (!currentUrl.startsWith("http")) {
      currentUrl = "http://" + currentUrl;
    }

    return new URL(currentUrl).hostname.replace("www.", "");
  };

  return (
    <Dialog open onClose={handleClose} classes={{ paper: styles.paper }}>
      <DialogTitle onClose={handleClose} hasCloseButton mobileMode={isMobile} />
      {showSuccess ? (
        <>
          <DialogContent classes={{ root: styles.dialogSuccessContent }}>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={`/images/interested-job-success.svg`}
              alt="icon"
              className={styles.successImage}
            />
            <Typography variant="h6" className={styles.successTitle}>
              Done! You&apos;ve expressed your interest for the position of{" "}
              {job?.role} - {Boolean(job?.sub_role) ? job?.sub_role : ""} in{" "}
              {job?.city}.
            </Typography>
            <Typography
              color="text.secondary"
              variant="subtitle1"
              className={styles.successDescription}
            >
              Our team will review your interest and if they too think you are
              suitable you&apos;ll be presented to our hiring partner. If we
              don&apos;t feel you are suitable your interest won&apos;t be
              forwarded.
              <br />
              Please note that you may only express interest in up to 5 jobs.
            </Typography>
            <Button className={styles.successButton} onClick={handleClose}>
              Great
            </Button>
          </DialogContent>
        </>
      ) : (
        <>
          <DialogContent classes={{ root: styles.dialogContent }}>
            {isLoading ? (
              <Skeleton
                width={47}
                height={47}
                variant="rectangular"
                className={styles.imageLoadingMobile}
              />
            ) : (
              <>
                {!showFallbackImage && (
                  <>
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      alt="logo"
                      src={logoImageUrl}
                      className={styles.logoContainerMobile}
                      onError={addFallbackImage}
                    />
                  </>
                )}
              </>
            )}
            <div className={styles.topContainer}>
              <div className={styles.titleContainer}>
                <div className={styles.roleContainer}>
                  {isLoading ? (
                    <Skeleton variant="text" width={160} />
                  ) : (
                    <Typography variant="h5" className={styles.title}>
                      {job?.role}
                    </Typography>
                  )}
                  {Boolean(job?.sub_role) && (
                    <Typography
                      variant="subtitle1"
                      className={styles.subtitle}
                      color="text.secondary"
                    >
                      {isMobile ? (
                        job?.sub_role
                      ) : (
                        <>&nbsp;.&nbsp;{job?.sub_role}</>
                      )}
                    </Typography>
                  )}
                </div>
                {isLoading ? (
                  <Skeleton variant="text" width={300} />
                ) : (
                  <div
                    className={cx(styles.titleDetailsContainer, {
                      [styles.externalTitleDetailsContainer]: isExternal,
                    })}
                  >
                    <div className={styles.titleDetailsItem}>
                      <Tooltip
                        title="City"
                        placement={
                          isMobile ? "bottom" : isExternal ? "right" : "bottom"
                        }
                      >
                        <span>
                          <LocationIcon className={styles.detailsIcon} />
                        </span>
                      </Tooltip>

                      <Typography
                        variant="body2"
                        className={styles.titleDetailText}
                      >
                        {job?.city}
                      </Typography>
                    </div>

                    <div className={styles.titleDetailsItem}>
                      <Tooltip
                        title="Company"
                        placement={
                          isMobile ? "bottom" : isExternal ? "right" : "bottom"
                        }
                      >
                        <span>
                          <BuildingIcon className={styles.detailsIcon} />
                        </span>
                      </Tooltip>

                      <Typography
                        variant="body2"
                        className={styles.titleDetailText}
                      >
                        {job?.partner_name}
                      </Typography>
                    </div>

                    {isExternal &&
                      (Boolean(job?.salary_from) || Boolean(job.salary_to)) && (
                        <div className={styles.titleDetailsItem}>
                          <Tooltip
                            title="Salary"
                            placement={
                              isMobile
                                ? "bottom"
                                : isExternal
                                ? "right"
                                : "bottom"
                            }
                          >
                            <span>
                              <GlobalDollarIcon
                                className={styles.detailsIcon}
                              />
                            </span>
                          </Tooltip>
                          <Typography
                            variant="body2"
                            className={styles.titleDetailText}
                          >
                            {`${job?.salary_from ? "$" : ""}${
                              job?.salary_from || ""
                            }${job.salary_from && job.salary_to ? " - " : ""}${
                              job?.salary_to ? "$" : ""
                            }${job?.salary_to || ""} ${
                              job?.salary_type == "hourly"
                                ? "per hour"
                                : job?.salary_type || ""
                            }`}
                          </Typography>
                        </div>
                      )}

                    <div className={styles.titleDetailsItem}>
                      <Tooltip
                        title="Industry"
                        placement={
                          isMobile ? "bottom" : isExternal ? "right" : "bottom"
                        }
                      >
                        <span>
                          <IndustryIcon className={styles.detailsIcon} />
                        </span>
                      </Tooltip>

                      <Typography
                        variant="body2"
                        className={styles.titleDetailText}
                      >
                        {job?.industry}
                      </Typography>
                    </div>
                    {!isExternal && (
                      <div className={styles.titleDetailsItem}>
                        <Tooltip
                          title="Starting Date"
                          placement={
                            isMobile
                              ? "bottom"
                              : isExternal
                              ? "right"
                              : "bottom"
                          }
                        >
                          <span>
                            <CalenderCheckIcon className={styles.detailsIcon} />
                          </span>
                        </Tooltip>

                        <Typography
                          variant="body2"
                          className={styles.titleDetailText}
                        >
                          {jobStartDateLabel}
                        </Typography>
                      </div>
                    )}
                    {categoryObj && (
                      <div className={styles.titleDetailsItem}>
                        {categoryObj?.icon}
                        <Typography
                          variant="body2"
                          className={styles.titleDetailText}
                        >
                          {categoryObj?.title}
                        </Typography>
                      </div>
                    )}
                  </div>
                )}
              </div>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              {isLoading ? (
                <Skeleton width={47} height={47} variant="rectangular" />
              ) : (
                <>
                  {!showFallbackImage && (
                    <>
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img
                        alt="logo"
                        src={logoImageUrl}
                        className={styles.logoContainer}
                      />
                    </>
                  )}
                </>
              )}
            </div>
            <Divider className={styles.divider} />
            <div className={styles.content}>
              {isLoading ? (
                <Skeleton variant="text" width={300} />
              ) : (
                <div className={styles.contentTitleContainer}>
                  <Typography
                    variant="subtitle1"
                    className={styles.contentTitle}
                  >
                    {isExternal ? "Details" : `About ${job?.partner_name}`}
                  </Typography>
                  {Boolean(job?.website) && (
                    <div
                      className={styles.websiteContainer}
                      onClick={handleOpenWebsite}
                    >
                      <Typography color="primary">Website</Typography>
                      <RedirectIcon className={styles.redirectIcon} />
                    </div>
                  )}
                </div>
              )}

              {isLoading ? (
                <>
                  <Skeleton variant="text" width={300} />
                  <Skeleton variant="text" width={300} />
                  <Skeleton variant="text" width={100} />
                </>
              ) : (
                <>
                  {isExternal ? (
                    <Typography
                      variant="body2"
                      className={styles.contentDescription}
                      // component={}
                      dangerouslySetInnerHTML={{
                        __html: job?.description || "",
                      }}
                    ></Typography>
                  ) : (
                    <Typography variant="body2" className={styles.contentAbout}>
                      {job?.about}
                    </Typography>
                  )}
                </>
              )}
              {!isExternal && (
                <>
                  {isLoading ? (
                    <>
                      <Skeleton variant="text" width={60} />
                    </>
                  ) : (
                    <Typography
                      variant="subtitle1"
                      className={styles.contentTitle}
                    >
                      Position
                    </Typography>
                  )}
                  {isLoading ? (
                    <>
                      <Skeleton variant="text" width={300} />
                      <Skeleton variant="text" width={300} />
                      <Skeleton variant="text" width={100} />
                    </>
                  ) : (
                    <Typography
                      variant="body2"
                      className={styles.contentInternalAbout}
                    >
                      {job?.description}
                    </Typography>
                  )}
                </>
              )}
              {/* <ul className={styles.listItemsContainer}>
                {requirements.map((each, idx) => (
                  <Typography
                    key={idx}
                    variant="body2"
                    className={styles.listItem}
                  >
                    {each}
                  </Typography>
                ))}
              </ul> */}
              {isLoading ? (
                <Skeleton variant="text" className={styles.contentTitle} />
              ) : (
                <>
                  {skills.length > 0 && (
                    <Typography
                      variant="subtitle1"
                      className={styles.contentTitle}
                    >
                      Required Skills
                    </Typography>
                  )}
                </>
              )}
              {!isSuperliteTrip && !isWHArrivalOnly && (
                <div className={styles.skillsContainer}>
                  {isLoading
                    ? [1, 2, 3].map((each) => (
                        <Skeleton
                          key={each}
                          className={styles.skillTagLoader}
                          variant="rectangular"
                        />
                      ))
                    : skills.map((eachSkill) => (
                        <Tag
                          key={eachSkill}
                          label={eachSkill}
                          color="secondary"
                          size="small"
                          className={styles.skillTag}
                        />
                      ))}
                </div>
              )}
              {isLoading ? (
                <Skeleton variant="text" className={styles.contentTitle} />
              ) : (
                <>
                  {job?.perk_benefit && job.perk_benefit.length > 0 && (
                    <>
                      <Typography
                        variant="subtitle1"
                        className={styles.perkTitle}
                      >
                        Perks & Benefits
                      </Typography>
                      <Typography
                        variant="body2"
                        className={styles.contentAbout}
                      >
                        {job?.perk_benefit}
                      </Typography>
                    </>
                  )}
                </>
              )}
              {canExpressInterestInJob && !isInterested && (
                <div className={styles.conditionContainer}>
                  <Checkbox
                    color="primary"
                    checked={isAgreedToTerms || isInterested}
                    disabled={isInterested}
                    onChange={(event) =>
                      setIsAgreedToTerms(event.target.checked)
                    }
                    className={styles.checkbox}
                  />
                  <Typography variant="subtitle2">
                    I acknowledge that if I express interest in this position, I
                    will accept an interview or job offer with this
                    organisation, if selected. Expressing interest does not
                    guarantee an interview or job offer.
                  </Typography>
                </div>
              )}
            </div>
          </DialogContent>
          <DialogActions className={styles.actionContainer}>
            {canExpressInterestInJob && (
              <Tooltip
                title={
                  disableShowInterestButton
                    ? "This feature unlocks once your resume is approved."
                    : ""
                }
              >
                <div className={styles.interestButtonContainer}>
                  <Button
                    onClick={handleInterest}
                    disabled={
                      isMutating ||
                      isInterested ||
                      disableShowInterestButton ||
                      !isAgreedToTerms
                    }
                    startIcon={
                      isInterested ? (
                        <CheckedIcon variant="outlined" />
                      ) : undefined
                    }
                    className={styles.interestButton}
                  >
                    {isInterested ? "Interested" : "I'm Interested"}
                  </Button>
                </div>
              </Tooltip>
            )}
            {isExternal && (
              <Button
                onClick={goToJob}
                disabled={isMutating || isInterested}
                className={styles.actionButton}
              >
                Go to job
              </Button>
            )}
            <Button
              onClick={handleSave}
              variant="outlined"
              color="info"
              disabled={isMutating}
              className={styles.actionButton}
            >
              {isSaved ? "Unsave Job" : "Save Job"}
            </Button>
            {isExternal && (
              <Typography variant="caption" className={styles.link}>
                As shown on{" "}
                <Typography variant="caption" component="a" onClick={goToJob}>
                  {getDomain()}
                </Typography>
              </Typography>
            )}
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default JobDialog;
